import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import emailIcon from '../assets/images/email.png';
import padlockIcon from '../assets/images/padlock.png';
import checkIcon from '../assets/images/checked.png';
import backIcon from '../assets/images/arrow_back.png';
import { useAuth } from '../hooks/useAuth';

type ForgotPasswordStep = 'email' | 'code' | 'reset';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { sendVerificationCode, resetPasswordWithCode, isLoading } = useAuth();
  const [step, setStep] = useState<ForgotPasswordStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [nnewPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countdown, setCountdown] = useState(0);

  const handleSendCode = async () => {
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    const result = await sendVerificationCode(email);
    if (result.success) {
      setStep('code');
      setCountdown(60); // 60 seconds countdown
      
      // Start countdown
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (result.error == 'A verification code was sent recently.') {
      setStep('code');
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim() || code.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    // For verification, we just need to check if the code is valid
    // The actual verification happens when we reset the password
    setStep('reset');
    // toast.success('Code verified successfully');
  };

  const handleResetPassword = async () => {
    if (!nnewPassword.trim() || !confirmPassword.trim()) {
      toast.error('Please enter both password fields');
      return;
    }

    if (nnewPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (nnewPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    const result = await resetPasswordWithCode({
      email,
      code,
      password: nnewPassword,
      password_confirmation: confirmPassword
    });

    if (result.success) {
      console.log(JSON.stringify(result));
      navigate('/login');
    }
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    
    handleSendCode();
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white`}>
          1
        </div>
        <div className={`w-16 h-1 ${step === 'code' || step === 'reset' ? 'bg-primary' : 'bg-grey_light'}`}></div>
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step === 'code' || step === 'reset' ? 'bg-primary text-white' : 'bg-grey_light text-grey_dark'}`}>
          2
        </div>
        <div className={`w-16 h-1 ${step === 'reset' ? 'bg-primary' : 'bg-grey_light'}`}></div>
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step === 'reset' ? 'bg-primary text-white' : 'bg-grey_light text-grey_dark'}`}>
          3
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 'email':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-medium text-dark mb-2">Forgot Password</h2>
              <p className="text-grey_dark">
                Enter your email address and we'll send you a verification code to reset your password
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2 text-dark">
                Email Address
              </label>
              <Input
                leftIcon={emailIcon}
                attributes={{
                  id: "email",
                  type: "email",
                  placeholder: "Enter your email",
                  value: email,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
                  required: true
                }}
                className="w-full"
              />
            </div>

            <Button
              onClick={handleSendCode}
              variant="filled-primary"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Verification Code'}
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-primary hover:text-primary-variant font-medium transition-colors duration-200 inline-flex items-center"
              >
                <img src={backIcon} alt="Back" className="w-4 h-4 mr-2" />
                Back to Login
              </Link>
            </div>
          </div>
        );

      case 'code':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-medium text-dark mb-2">Enter Verification Code</h2>
              <p className="text-grey_dark">
                We've sent a 6-digit code to <span className="font-medium">{email}</span>
              </p>
            </div>

            <div>
              <label htmlFor="code" className="block text-sm font-medium mb-2 text-dark">
                6-Digit Verification Code
              </label>
              <Input
                attributes={{
                  id: "code",
                  type: "text",
                  placeholder: "Enter 6-digit code",
                  value: code,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(value);
                  },
                  required: true,
                  maxLength: 6
                }}
                className="w-full text-center text-2xl tracking-widest"
              />
            </div>

            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-grey_dark">
                  Resend code in <span className="font-medium">{countdown}s</span>
                </p>
              ) : (
                <button
                  onClick={handleResendCode}
                  className="text-primary hover:text-primary-variant font-medium transition-colors duration-200"
                >
                  Resend verification code
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep('email')}
                variant="google"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleVerifyCode}
                variant="filled-primary"
                className="flex-1"
                disabled={isLoading || code.length !== 6}
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </div>
          </div>
        );

      case 'reset':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-medium text-dark mb-2">Reset Your Password</h2>
              <p className="text-grey_dark">
                Create a new password for your account
              </p>
            </div>
            <div></div>

            <div>
              <label htmlFor="nnewPassword" className="block text-sm font-medium mb-2 text-dark">
                New Password
              </label>
              <Input
                leftIcon={padlockIcon}
                showPasswordToggle={true}
                attributes={{
                  id: "nnewPassword",
                  placeholder: "Enter new password",
                  value: nnewPassword,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value),
                  required: true
                }}
                className="w-full"
                type="password"
              />
              <p className="text-xs text-grey_dark mt-1">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2 text-dark">
                Confirm New Password
              </label>
              <Input
                leftIcon={padlockIcon}
                showPasswordToggle={true}
                attributes={{
                  id: "confirmPassword",
                  placeholder: "Confirm new password",
                  value: confirmPassword,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value),
                  required: true
                }}
                className="w-full"
                type="password"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep('code')}
                variant="google"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleResetPassword}
                variant="filled-primary"
                className="flex-1"
                disabled={isLoading || !nnewPassword || !confirmPassword}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-bg_grey_light">
      <div className="max-w-md w-full bg-light p-8 rounded-lg shadow-md">
        {renderStepIndicator()}
        {renderStepContent()}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;